import {
  forwardRef,
  type JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import styled from 'styled-components';
import { SearchFieldWithI18n } from '@frameio/shared/components/SearchField';
import { Box, Separator, Text, theme } from '@frameio/vapor';
import { IconMagnifyingglass } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/MemberSearch';

TEXT.DEFINE({
  /** Placeholder text for search input field */
  inputPlaceholder: 'Search for user name or email',

  /** Header shown above no results message */
  noResultsHeader: 'Search result',

  /** Message displaying search query with no results */
  noResults: (query: string) => `No result found for "${query}"`,
});

type MemberSearchProps = {
  adornmentAfter?: JSX.Element;
  currentValue?: string;
  loading?: boolean;
  onChange: (query: string) => void;
  placeholder?: string;
  showNoResults?: boolean;
};

export const MemberSearch = forwardRef(
  (
    {
      adornmentAfter,
      currentValue = '',
      loading,
      onChange,
      placeholder = TEXT.inputPlaceholder,
      showNoResults = false,
    }: MemberSearchProps,
    forwardedRef
  ) => {
    const inputRef = useRef(undefined);
    const [inputHasFocus, setInputHasFocus] = useState(false);
    const [inputValue, setInputValue] = useState(currentValue);
    const setInputHasFocusFalse = useCallback(
      () => setInputHasFocus(false),
      []
    );
    const setInputHasFocusTrue = useCallback(() => setInputHasFocus(true), []);
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape' && inputHasFocus) {
          event.nativeEvent.stopImmediatePropagation();
          event.preventDefault();
          onChange('');
        }
      },
      [inputHasFocus, onChange]
    );
    useEffect(() => {
      setInputValue(currentValue);
    }, [currentValue]);
    return (
      <>
        <Box
          display="flex"
          justifyContent="space-between"
          paddingBottom={8}
          paddingX={16}
          position="relative"
          style={{ zIndex: theme.zIndices.modal }}
          width="100%"
        >
          <Box width="100%">
            <SearchFieldWithI18n
              ref={mergeRefs([forwardedRef, inputRef])}
              debounce={200}
              enableSpinner={false}
              isSearching={loading}
              onBlur={setInputHasFocusFalse}
              onFocus={setInputHasFocusTrue}
              onKeyDown={handleKeyDown}
              onQueryChange={setInputValue}
              onRequestSearch={onChange}
              placeholder={placeholder}
              query={inputValue}
            />
          </Box>
          {adornmentAfter && <Box paddingLeft={8}>{adornmentAfter}</Box>}
        </Box>

        {showNoResults && (
          <Box paddingTop={6} paddingX={16}>
            <Text color="tertiary" variant="body">
              {TEXT.noResultsHeader}
            </Text>

            <Box
              alignItems="center"
              display="flex"
              paddingBottom={2}
              paddingLeft={4}
              paddingTop={24}
            >
              <IconMagnifyingglass color={theme.colors.icon.interactive.off} />

              <StyledText color="tertiary" variant="secondary">
                {TEXT.noResults(currentValue)}
              </StyledText>
            </Box>
            <Separator color="primary" gap={8} style={{ marginLeft: 40 }} />
          </Box>
        )}
      </>
    );
  }
);

const StyledText = styled(Text)`
  padding-left: ${theme.spaces[12]};
`;
